import React, { useState, useEffect } from 'react';
import { KTSVG, toAbsoluteUrl } from '../../../_metronic/helpers';

const SummaryCard = () => {
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [loading, setLoading] = useState<boolean>(false);

    // States for animated values
    const [shipmentStarted, setShipmentStarted] = useState(0);
    const [shipmentEnded, setShipmentEnded] = useState(0);
    const [shipmentBooked, setShipmentBooked] = useState(0);
    const [deliveredCount, setDeliveredCount] = useState(0);

    const [dashboardData, setDashboardData] = useState({
        shipment_started_count_shipment: 0,
        shipment_ended_count_shipment: 0,
        shipment_booked_count: 0,
        delivered_count_shipment: 0,
    });

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    useEffect(() => {
        fetchDashboardData(selectedMonth, year);
    }, [selectedMonth, year]);

    const animateCount = (targetValue: number, setValue: React.Dispatch<React.SetStateAction<number>>) => {
        let start = 0;
        const duration = 2000; // 2 seconds animation
        const increment = targetValue / (duration / 30);

        const updateCount = () => {
            start += increment;
            if (start >= targetValue) {
                setValue(targetValue);
            } else {
                setValue(Math.round(start));
                requestAnimationFrame(updateCount);
            }
        };

        requestAnimationFrame(updateCount);
    };

    useEffect(() => {
        animateCount(dashboardData.shipment_started_count_shipment, setShipmentStarted);
        animateCount(dashboardData.shipment_ended_count_shipment, setShipmentEnded);
        animateCount(dashboardData.shipment_booked_count, setShipmentBooked);
        animateCount(dashboardData.delivered_count_shipment, setDeliveredCount);
    }, [dashboardData]);

    const fetchDashboardData = async (monthIndex: number, selectedYear: number) => {
        try {
            setLoading(true);

            const response = await fetch('https://backendcway.causewaycargo.com/superadmin/dashboarddetails', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    auth: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzM3MDA4MTc1fQ.FF6USnon0RR_0FIWWJGbzQQN8hX_7uEdyn9vK_2iW1s',
                },
                body: JSON.stringify({
                    month: `${months[monthIndex - 1]} ${selectedYear}`,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();

            if (result.status === 'success' && result.data) {
                const {
                    shipment_started_count_shipment,
                    shipment_ended_count_shipment,
                    shipment_booked_count,
                    delivered_count_shipment,
                } = result.data;

                setDashboardData({
                    shipment_started_count_shipment: Number(shipment_started_count_shipment) || 0,
                    shipment_ended_count_shipment: Number(shipment_ended_count_shipment) || 0,
                    shipment_booked_count: Number(shipment_booked_count) || 0,
                    delivered_count_shipment: Number(delivered_count_shipment) || 0,
                });
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 rounded w-100 d-flex flex-column" style={{ height: '300px', backgroundColor: '#FFFFFF' }}>
            <div className="d-flex justify-content-between align-items-center mb-5">
                <h3 className="fs-4 fw-bold text-dark mb-0">Shipment Details</h3>

                <div className="d-flex align-items-center">
                    <span onClick={() => setSelectedMonth((prev) => (prev === 1 ? 12 : prev - 1))} className="d-flex" style={{ cursor: 'pointer' }}>
                        <KTSVG path='/media/icons/duotune/arrows/arr074.svg' className='svg-icon-2x svg-icon-primary d-block' />
                    </span>
                    <span className="month-display mx-2">
                        {months[selectedMonth - 1]} {year}
                    </span>
                    <span onClick={() => setSelectedMonth((prev) => (prev === 12 ? 1 : prev + 1))} className="d-flex" style={{ cursor: 'pointer' }}>
                        <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2x svg-icon-primary d-block' />
                    </span>
                </div>
            </div>

            <div className="d-flex flex-column justify-content-between flex-grow-1">
                <div className="row g-4 mb-4">
                    <div className="col-6">
                        <div className="p-4 rounded-3 d-flex align-items-center" style={{ height: '100px', backgroundColor: '#F3F6F8' }}>
                            <div className="d-flex align-items-start justify-content-between w-100">     
                                <div>
                                    <p className="fs-2 fw-bold mb-0">{shipmentStarted}</p>
                                    <p className="fs-5 text-muted mb-0">Started</p>
                                </div>
                                <div className="me-3">
                                    <img alt='icon' src={toAbsoluteUrl('/media/icons/duotune/general/icon1.svg')} style={{ width: '25px' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="p-4 rounded-3 d-flex align-items-center" style={{ height: '100px', backgroundColor: '#F3F6F8' }}>
                            <div className="d-flex align-items-start justify-content-between w-100">
                                <div>
                                    <p className="fs-2 fw-bold mb-0">{shipmentEnded}</p>
                                    <p className="fs-5 text-muted mb-0">Ended</p>
                                </div>
                                <div className="me-3">
                                    <img alt='icon' src={toAbsoluteUrl('/media/icons/duotune/general/icon2.svg')} style={{ width: '25px' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-6">
                        <div className="p-4 rounded-3 d-flex align-items-center" style={{ height: '100px', backgroundColor: '#F3F6F8' }}>
                            <div className="d-flex align-items-start justify-content-between w-100">
                                <div>
                                    <p className="fs-2 fw-bold mb-0">{shipmentBooked}</p>
                                    <p className="fs-5 text-muted mb-0">Booked</p>
                                </div>
                                <div className="me-3">
                                    <img alt='icon' src={toAbsoluteUrl('/media/icons/duotune/general/icon3.svg')} style={{ width: '25px' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="p-4 rounded-3 d-flex align-items-center" style={{ height: '100px', backgroundColor: '#F3F6F8' }}>
                            <div className="d-flex align-items-start justify-content-between w-100">
                                <div>
                                    <p className="fs-2 fw-bold mb-0">{deliveredCount}</p>
                                    <p className="fs-5 text-muted mb-0">Delivered</p>
                                </div>
                                <div className="me-3">
                                    <img alt='icon' src={toAbsoluteUrl('/media/icons/duotune/general/icon4.svg')} style={{ width: '25px' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SummaryCard;
